import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as queryString from 'query-string';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { logError } from '@/imports/logging/ClientLogger';
import { accountsClientPassword } from '@/modules/accounts/accounts-client';
import {
  ACCESS_TOKEN_COOKIE_KEY,
  tokenStorage,
} from '@/modules/accounts/tokenStorage';
import { EMAIL_PLACEHOLDER, PASSWORD_PLACEHOLDER, REG_EXP } from '@/utilities';
import { Editor, EditorState, convertFromRaw, CompositeDecorator } from 'draft-js';
import 'draft-js/dist/Draft.css';

type LogInFormValues = {
  email: string;
  password: string;
};


// Add these link handling functions
const Link = (props: any) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#3182ce', textDecoration: 'underline' }}
    >
      {props.children}
    </a>
  );
};

const findLinkEntities = (contentBlock: any, callback: any, contentState: any) => {
  contentBlock.findEntityRanges(
    (character: any) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'LINK'
      );
    },
    callback
  );
};

// Create the decorator
const decorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: Link,
  },
]);

export const LogInForm: React.FC<{
  acronym?: string;
  onClickResetPassword: () => void;
  header?: string;
  ssoProvider?: string;
  /** Hide "Sign in with your Tradewing Account" when SSO is shown (Morgoth flag). */
  hideTradewingLogin?: boolean;
  /** Always show email/password (e.g. /login/tradewing for staff). */
  forceTradewingLogin?: boolean;
  onSubmitLogin: () => void;
  onLoginOutcome: (outcome: string) => void;
  onClickSSO: () => void;
  loginContactMessage?: string;
}> = (props) => {
  const router = useRouter();

  const showSsoButton = !!props.ssoProvider && !props.forceTradewingLogin;
  const allowTradewingLoginExpand =
    showSsoButton && !props.hideTradewingLogin;

  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [showLoginInputs, setShowLoginInputs] = React.useState(
    !showSsoButton || props.forceTradewingLogin
  );

  const emailFromQuery =
    typeof router.query.email === 'string' ? router.query.email : '';

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitted, isValid },
  } = useForm<LogInFormValues>({
    defaultValues: {
      email: emailFromQuery,
      password: '',
    },
  });

  React.useEffect(() => {
    if (!router.isReady || !emailFromQuery) return;
    reset({
      email: emailFromQuery,
      password: '',
    });
  }, [emailFromQuery, reset, router.isReady]);

  const onSubmit = async ({
    email,
    password,
  }: LogInFormValues): Promise<void> => {
    props.onSubmitLogin();
    setIsLoading(true);
    setHasError(false);
    try {
      await accountsClientPassword.login({ user: { email }, password });

      try {
        if (typeof window !== 'undefined') {
          const meteorToken =
            window.localStorage?.getItem('Meteor.loginToken') ||
            window.localStorage?.getItem('meteor_login_token');
          if (meteorToken) {
            tokenStorage.setItem(ACCESS_TOKEN_COOKIE_KEY, meteorToken);
          }
        }
      } catch {
      }

      props.onLoginOutcome('tw-login-success');
      window.location.reload();
    } catch (err) {
      setIsLoading(false);
      logError(err);
      props.onLoginOutcome(
        err.message === 'User not found [403]'
          ? 'tw-login-error-wrong-email'
          : 'tw-login-error-wrong-pw'
      );
      setHasError(true);
    }
  };

  return (
    <Stack as="form" spacing="6" noValidate onSubmit={handleSubmit(onSubmit)}>
      <Heading size="md">Sign in to your Account</Heading>

      {!!hasError && (
        <Alert status="error">
          <AlertIcon />
          {showSsoButton ? (
            <AlertDescription>
              There was a problem logging into this account. If you already have
              a membership with {props.acronym || 'the association'}, try the{' '}
              {props.acronym || 'association'} login method. Otherwise, try a
              different username or password.
            </AlertDescription>
          ) : (
            <AlertDescription>
              There was a problem logging into this account. Try a different
              username or password.
            </AlertDescription>
          )}
        </Alert>
      )}

      {!!props.header && (
        <Text align="center" color="gray.500">
          {props.header}
        </Text>
      )}

      {showSsoButton && (
        <>
          <Button
            w="full"
            height="auto"
            whiteSpace="normal"
            py="2.5"
            type="button"
            onClick={() => {
              props.onClickSSO();
              router.push(
                `/v1/sso?${queryString.stringify({
                  redirect: router.asPath,
                })}`
              );
            }}
          >
            Login with your Existing{' '}
            {props.acronym ? props.acronym : 'Association'} Account
          </Button>

          {allowTradewingLoginExpand && (
            <Stack direction="row" alignItems="center">
              <Divider borderColor="gray.100" />
              <Text textStyle="compactStrong" color="gray.500">
                OR
              </Text>
              <Divider borderColor="gray.100" />
            </Stack>
          )}
        </>
      )}

      {showLoginInputs ? (
        <Stack spacing="6" mb="6">
          <FormControl isRequired isInvalid={!!errors.email}>
            <FormLabel requiredIndicator={<></>}>Email</FormLabel>
            <Input
              autoFocus
              type="email"
              placeholder={EMAIL_PLACEHOLDER}
              {...register('email', {
                required: 'Email is required',
                validate: {
                  email: (value) =>
                    !value ||
                    REG_EXP.EmailWithTLD.test(value) ||
                    'You must enter a valid email address',
                },
              })}
            />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.password}>
            <FormLabel requiredIndicator={<></>}>Password</FormLabel>
            <Input
              type="password"
              placeholder={PASSWORD_PLACEHOLDER}
              {...register('password', { required: 'Password is required' })}
              autoComplete="current-password"
            />
            <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
          </FormControl>

          <Stack>
            <Button
              type="submit"
              w="full"
              isLoading={isLoading}
              variant={showSsoButton ? 'outline' : 'solid'}
              isDisabled={isSubmitted && !isValid}
              borderColor="primary.500"
            >
              Sign In
            </Button>

            <Box color="gray.600">
              <Button
                variant="link"
                colorScheme="black"
                onClick={props.onClickResetPassword}
              >
                Forgot your Password?
              </Button>
            </Box>
          </Stack>
        </Stack>
      ) : allowTradewingLoginExpand ? (
        <Button
          variant="link"
          colorScheme="black"
          fontWeight="normal"
          onClick={() => setShowLoginInputs(true)}
        >
          Sign in with your Tradewing Account
        </Button>
      ) : null}


{props.loginContactMessage && (
  <Box 
    fontSize="sm" 
    color="gray.600"
    textAlign="center"
    width="100%"
    mt={4}
    mx={'auto'}

  >
    {(() => {
      try {
        const parsedContent = JSON.parse(props.loginContactMessage);
        const contentState = convertFromRaw(parsedContent);
        const editorState = EditorState.createWithContent(contentState, decorator);

        return (
          <Box
            sx={{
              '.DraftEditor-root': {
                minHeight: 'auto',
                textAlign: 'center', 
              },
              '.public-DraftEditor-content': {
                minHeight: 'auto',
                display: 'inline-block',
                whiteSpace: 'pre-wrap', 
                width: '100%',
                justifyContent: "center"
              },
            }}
          >
            <Editor
              editorState={editorState}
              onChange={() => {}}
              readOnly={true}
              textAlignment='center'
            />
          </Box>
        );
      } catch (e) {
        console.error('Error rendering rich text:', e);
        return <Text textAlign="center">{props.loginContactMessage}</Text>;
      }
    })()}
  </Box>
)}


    </Stack>
  );
};
